import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { InitialsService } from '../../../helpers/initial.service';
import { getAvatarBgColor } from '../../../helpers/avatar.helper';

@Component({
    selector: 'app-reassignment-table',
    imports: [CommonModule, FormsModule, ReactiveFormsModule],
    templateUrl: './reassignment-table.component.html',
    styleUrl: './reassignment-table.component.css'
})
export class ReassignmentTableComponent {
  @Input() formGroup!: FormGroup;
  @Input() fields: any;
  @Input() activeModalContext: any;
  @Output() selectionChange = new EventEmitter<any>();
  searchTerm: string = '';
  filteredTasks: any[] = [];
  visibleTasksCount = 5;
  defaultVisibleCount = 5;
  isLoading: boolean = true;
  constructor(private initialsService: InitialsService) {}
  ngOnInit() {
    this.filteredTasks = this.activeModalContext?.alreadyAssignedTasks || [];
    this.initializeAllSelected();

    this.isLoading = false;
  }

  toggleTaskSelection(task: any) {
    const control = this.formGroup.get(this.fields.fieldName);

    if (!control) return;

    const selectedIds: string[] = control.value || [];

    const index = selectedIds.indexOf(task._id);

    if (index > -1) {
      selectedIds.splice(index, 1);
    } else {
      selectedIds.push(task._id);
    }

    control.setValue([...selectedIds]);
    control.markAsDirty();
    control.updateValueAndValidity();
    this.selectionChange.emit({
      count: selectedIds.length,
      fieldName: this.fields.fieldName,
    });
  }

  isTaskSelected(task: any): boolean {
    const selectedIds = this.formGroup.get(this.fields.fieldName)?.value || [];
    return selectedIds.includes(task._id);
  }

  toggleSelectAll(event: any) {
    const control = this.formGroup.get(this.fields.fieldName);
    if (!control) return;

    if (event.target.checked) {
      const allIds = this.allTasks().map((task: any) => task._id);
      control.setValue(allIds);
    } else {
      control.setValue([]);
    }

    control.markAsDirty();
    control.updateValueAndValidity();
  }

  initializeAllSelected() {
    const control = this.formGroup.get(this.fields.fieldName);
    if (!control) return;
    const assignedTasks = this.activeModalContext?.alreadyAssignedTasks || [];
    const allIds = assignedTasks.map((task: any) => task._id);

    control.setValue(allIds);

    // mark as touched/dirty if needed
    control.markAsDirty();
    control.markAsTouched();

    // trigger validation
    control.updateValueAndValidity();
  }
  isIndeterminate(): boolean {
    const control = this.formGroup.get(this.fields.fieldName);
    const selectedIds: string[] = control?.value || [];
    const assignedTasks = this.activeModalContext?.alreadyAssignedTasks || [];
    const allIds = assignedTasks.map((task: any) => task._id);
    const totalTasks = allIds.length;

    return selectedIds.length > 0 && selectedIds.length < totalTasks;
  }

  isAllSelected(): boolean {
    const control = this.formGroup.get(this.fields.fieldName);
    const selectedIds: string[] = control?.value || [];
    const assignedTasks = this.activeModalContext?.alreadyAssignedTasks || [];
    const allIds = assignedTasks.map((task: any) => task._id);
    return selectedIds.length === allIds.length;
  }

  getInitials(name: string): string {
    return this.initialsService.getInitials(name);
  }

  /** Show color for the user department wise */
  getBgColorByDepartment(name: string): string {
    return getAvatarBgColor(name);
  }

  allTasks() {
    const assigned = this.activeModalContext?.alreadyAssignedTasks || [];
    const unassigned = this.activeModalContext?.unassignedTasks || [];

    // mark unassigned tasks
    const mappedUnassigned = unassigned.map((task: any) => ({
      ...task,
      isUnassigned: true,
    }));
    return [...assigned, ...mappedUnassigned];
  }

  trackByTask(index: number, task: any) {
    return task._id;
  }

  get visibleTasks() {
    return this.filteredTasks.slice(0, this.visibleTasksCount);
  }

  viewMore() {
    const total = this.filteredTasks.length;
    this.visibleTasksCount = Math.min(this.visibleTasksCount + 5, total);
  }

  viewLess() {
    this.visibleTasksCount = this.defaultVisibleCount;
  }

  get selectedTasksCount(): number {
    const control = this.formGroup.get(this.fields.fieldName);
    const selectedIds: string[] = control?.value || [];

    const assignedIds =
      this.activeModalContext?.alreadyAssignedTasks?.map((t: any) => t._id) ||
      [];

    return selectedIds.filter((id) => assignedIds.includes(id)).length;
  }

  onSearch(value: any) {
    this.isLoading = true;

    const searchValue = (value || '').toLowerCase().trim();
    this.searchTerm = searchValue;

    const tasks = this.activeModalContext?.alreadyAssignedTasks || [];

    // If less than 3 characters → reset list
    if (searchValue.length < 3) {
      this.filteredTasks = tasks;
      this.visibleTasksCount = this.defaultVisibleCount;
      this.isLoading = false; // ✅ stop skeleton
      return;
    }

    this.filteredTasks = tasks.filter(
      (task: any) =>
        (task.taskId || '').toLowerCase().includes(searchValue) ||
        (task.control || '').toLowerCase().includes(searchValue) ||
        (task.id || '').toLowerCase().includes(searchValue) ||
        (task.name || '').toLowerCase().includes(searchValue) ||
        (task.assetTag || '').toLowerCase().includes(searchValue) ||
        (task.currentAssignee?.name || '')
          .toLowerCase()
          .includes(searchValue) ||
        (task.currentAssignee?.email || '')
          .toLowerCase()
          .includes(searchValue) ||
        (task.previousAssignee?.name || '')
          .toLowerCase()
          .includes(searchValue) ||
        (task.previousAssignee?.email || '')
          .toLowerCase()
          .includes(searchValue),
    );

    this.visibleTasksCount = this.defaultVisibleCount;

    this.isLoading = false; // ✅ stop skeleton
  }

  clearSearch(input: HTMLInputElement) {
    input.value = '';
    this.searchTerm = '';
    this.onSearch('');
    input.focus();
  }

  get showNothingToSubmit(): boolean {
    return (
      (this.activeModalContext?.unassignedTasks || []).length === 0 &&
      this.selectedTasksCount === 0
    );
  }
}
